/* eslint-disable */
import React from 'react';
import styled from 'styled-components';
import type { Coordinates } from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import searchStateContext from '../../../contexts/map-context';
import type { Dealer } from '../../../models/dealer';
import Autocompletion from '../../common/Autocompletion/Autocompletion';
import { placeDetails } from '../../../utils/geocodeDetails';
import type { City } from '../../../models/common/places';
import loggerContext from '../../../contexts/logger-context';
import { getServices } from '../../../utils/search';
import trackingContext from '../../../contexts/tracking-context';
import {
  trackPlaceSelectionAction,
  trackDealerDetailsDisplayAction,
  trackDealerSelectionAction,
} from '../../../utils/trackingUtils';
import { useURLService } from '../../../hooks/useURLService';

export default function MapAutocompletion() {
  const {
    isGoogleMapsApiLoaded,
    searchText,
    locale,
    intlSearchEnabled,
    results,
    userSelectedFilters,
    showPartnerSuggestions,
  } = searchStateContext.useMapState();
  const { logger } = loggerContext.useLoggerState();
  const dispatch = searchStateContext.useMapDispatch();
  const { trackingService: tService } = trackingContext.useTrackingState();
  const generateURL = useURLService();

  const handlePlaceSelect = async (city: City, pos?: number) => {
    const currentUrl = window.location.href;

    try {
      dispatch({ type: ActionTypes.setSearchText, payload: city.name });
      dispatch({ type: ActionTypes.setQuery, payload: city.name });
      dispatch({ type: ActionTypes.setPlaceId, payload: city.placeId });
      const coordinates: Coordinates = await placeDetails(city.placeId);
      dispatch({ type: ActionTypes.setSearchGeoLocation, payload: coordinates });
      dispatch({ type: ActionTypes.setIsAutocompletionOpen, payload: false });
      dispatch({ type: ActionTypes.setSelectedDealerId, payload: null });
      dispatch({ type: ActionTypes.setDealerReviews, payload: [] });
      dispatch({ type: ActionTypes.setRatingSummary, payload: null });
      dispatch({ type: ActionTypes.setLastSubmittedSearch, payload: city.name });

      const payload = {
        'geolocation-lat': coordinates.lat,
        'geolocation-lng': coordinates.lng,
        filters: userSelectedFilters,
      };
      const url = generateURL(currentUrl, payload);
      trackPlaceSelectionAction(tService, city.name, 'MapAutocompletion', pos, url.href);
    } catch (err) {
      logger.error(err);
    }
  };

  const handleDealerSelect = (dealer: Dealer, pos?: number) => {
    const currentUrl = window.location.href;
    const { dealerId, additionalData, kvpsId, country } = dealer;
    const name = additionalData ? (additionalData.displayName ?? dealer.name) : dealer.name;
    const payload = {
      filters: userSelectedFilters,
      'dealer-id': dealerId,
      selected: kvpsId,
      country,
    };
    const url = generateURL(currentUrl, payload);
    trackDealerSelectionAction(tService, name, 'MapAutocompletion', searchText, pos, url.href);
    trackDealerDetailsDisplayAction(tService, dealer);

    dispatch({ type: ActionTypes.setSearchText, payload: name });
    dispatch({ type: ActionTypes.setSelectedDealerId, payload: dealer.dealerId });
    dispatch({
      type: ActionTypes.setResults,
      payload: { dealers: [dealer], services: getServices([dealer]) },
    });
    dispatch({
      type: ActionTypes.setSelectedPartner,
      payload: { tenant: country, kvpsid: kvpsId },
    });
    dispatch({ type: ActionTypes.setIsAutocompletionOpen, payload: false });
    dispatch({ type: ActionTypes.setShowRadius, payload: false });
    dispatch({ type: ActionTypes.resetGeolocationSearch });
    dispatch({ type: ActionTypes.setLastSubmittedSearch, payload: name });
  };

  const handleKeyboardSelect = (name: string) => {
    dispatch({ type: ActionTypes.setSearchText, payload: name });
  };

  const handleClose = () => {
    dispatch({ type: ActionTypes.setIsAutocompletionOpen, payload: false });
  };
  return (
    <SideBarAutocompletion data-test-id="map-autocompletion-component" searchResults={results}>
      <Autocompletion
        term={searchText}
        countryCode={locale.countryCode}
        intlSearchEnabled={intlSearchEnabled}
        isGoogleMapsApiLoaded={isGoogleMapsApiLoaded}
        showPartnerSuggestions={showPartnerSuggestions}
        onPlaceSelect={(city: City, pos?: number) => {
          void handlePlaceSelect(city, pos);
        }}
        onDealerSelect={handleDealerSelect}
        onKeyboardSelect={handleKeyboardSelect}
        onClose={handleClose}
        isOnMap
        data-test-id="component-autocompletion"
      />
    </SideBarAutocompletion>
  );
}

const SideBarAutocompletion = styled.div<{ searchResults: Dealer[] | null }>`
  overflow-y: auto;
  height: 542px;
  scrollbar-color: rgba(0, 0, 0, 0.4) transparent; /* optional for FF */
  scrollbar-width: thin; /* Firefox */

  /* For WebKit browsers (Chrome, Edge, Safari) */
  -webkit-overflow-scrolling: touch;

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }
`;
